import React, { useEffect, useRef } from "react";
import ChatInput from "./ChatInput";
import Toolbar from "./Toolbar";
import BotModeToggle from "./sub/BotModeToggle";

function ChatWindow({ conversation, messages, setMessages, quickNotes }) {
  const API_URL = process.env.REACT_APP_API_URL;
  const API_UPLOAD_URL =
    process.env.REACT_APP_API_UPLOAD_URL || "http://localhost:3001/api/upload";
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  function formatDate(timestamp) {
    return new Date(timestamp).toLocaleString("es-CO", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  //Obtener el id del usuario desde localStorage para mostrarlo en el menú, por ejemplo:
  const userStorage = localStorage.getItem("user");
  let userId = "";
  if (userStorage) {
    try {
      const userObj = JSON.parse(userStorage);
      userId = userObj.id;
    } catch (error) {
      console.error("Error al parsear el usuario de localStorage:", error);
    }
  }

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  if (!conversation) {
    return (
      <div className="chat-window empty">
        Selecciona una conversación para empezar
      </div>
    );
  }

  const handleSendMessage = (text, file = null, type = "text") => {
    // 1. Validación de seguridad (mantenemos tu lógica)
    if (conversation.user_id !== userId) {
      console.warn(
        "El userId de la conversación no coincide con el userId del localStorage. Asegúrate de que el mensaje se envía con el userId correcto.",
      );
      return; // No enviamos el mensaje si el userId no coincide
    }

    // 2. Usamos FormData en lugar de un objeto JSON normal
    const formData = new FormData();

    formData.append("conversationId", conversation.id);
    formData.append("phoneNumber", conversation.phone_number);
    formData.append("contac_name", conversation.contact_name);
    formData.append("userId", userId);
    formData.append("text", text);
    formData.append("type", type);
    formData.append("origenWeb", "true"); // Indicamos que el mensaje viene del frontend para que el backend no lo reenvíe al bot

    // 3. Si hay un archivo adjunto, lo empaquetamos con la clave 'mediaFile' (la misma que espera multer en el backend)
    if (file) {
      formData.append("mediaFile", file);
    }

    // 4. Enviamos la petición
    fetch(`${API_URL}send-message`, {
      method: "POST",
      // ⚠️ ALERTA MUY IMPORTANTE:
      // Fíjate que eliminamos el header "Content-Type: application/json".
      // Al pasar 'formData' en el body, el navegador automáticamente configurará
      // el Content-Type correcto como 'multipart/form-data' junto con los boundaries necesarios.
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        console.log("Mensaje enviado:", data);

        // Opcional: Aquí podrías hacer algo si falla, leyendo data.success
        if (!data.success) {
          console.error("Error desde el servidor:", data.error);
        }
      })
      .catch((err) => console.error("Error de red al enviar mensaje:", err));
  };

  // 🔥 NUEVA FUNCIÓN: Renderiza el contenido según el tipo de mensaje
  const renderMessageContent = (msg) => {
    // Busca la URL del archivo. Dependiendo de tu backend, podría venir en
    // msg.media_url o dentro del nuevo objeto msg.meta que agregaste.

    // cuando msg sea producto de consultar la bd debemos parsear msg.meta que viene como string a un objeto para acceder a sus propiedades, por ejemplo:
    //console.log("Renderizando mensaje:", msg);
    if (msg.meta && typeof msg.meta === "string") {
      try {
        msg.meta = JSON.parse(msg.meta);
      } catch (error) {
        console.error("Error al parsear msg.meta:", error);
        msg.meta = {}; // Asignamos un objeto vacío para evitar errores posteriores
      }
    }

    const fileUrl = msg.media_url || msg.meta?.url || "";

    // const fileUrl = msg.media_url || (msg.meta && msg.meta.url) || "";
    // console.log("Renderizando mensaje:", msg);
    switch (msg.type) {
      case "sticker": // Los stickers en WhatsApp suelen ser .webp y se renderizan como imagen
        return (
          <div className="message-media">
            <img
              src={`${API_UPLOAD_URL}${fileUrl}`}
              alt={msg.type === "sticker" ? "Sticker" : "Imagen enviada"}
              crossOrigin="anonymous"
              className="message-image"
              style={{
                maxWidth: "100%",
                maxHeight: msg.type === "sticker" ? "120px" : "300px", // Los stickers son más pequeños
                borderRadius: "8px",
                marginBottom: msg.body ? "8px" : "0",
              }}
            />
            {/* Si enviaron una imagen con un "caption" (texto), se renderiza abajo */}
            {msg.body && <div className="message-body">{msg.body}</div>}
          </div>
        );

      case "image":
        return (
          <div className="message-media">
            <img
              src={`${API_UPLOAD_URL}${fileUrl}`} // Asegúrate de que esta URL sea correcta según tu backend
              alt="Imagen enviada"
              crossOrigin="anonymous" // Esto es importante para evitar problemas de CORS al mostrar imágenes desde tu backend
              className="message-image"
              style={{
                maxWidth: "100%",
                maxHeight: "300px",
                borderRadius: "8px",
                marginBottom: msg.body ? "8px" : "0", // Si hay texto (caption), dejamos un espacio debajo de la imagen
              }}
            />
            {/* Si enviaron una imagen con un "caption" (texto), se renderiza abajo */}
            {msg.body && (
              <div className="message-body">
                {msg.meta?.caption ? msg.meta.caption : msg.body}
              </div>
            )}
          </div>
        );

      case "audio":
        return (
          <div className="message-media">
            <audio
              controls
              src={`${API_UPLOAD_URL}${fileUrl}`}
              style={{ maxWidth: "250px" }}
            >
              Tu navegador no soporta el elemento de audio.
            </audio>
          </div>
        );

      case "video":
        return (
          <div className="message-media">
            <video
              controls
              src={`${API_UPLOAD_URL}${fileUrl}`}
              style={{
                maxWidth: "100%",
                maxHeight: "300px",
                borderRadius: "8px",
              }}
            >
              Tu navegador no soporta el elemento de video.
            </video>
            {msg.body && <div className="message-body">{msg.body}</div>}
          </div>
        );

      case "document":
        // msg.body suele traer el nombre del archivo original
        return (
          <div
            className="message-media document-message"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px",
              background: "rgba(0,0,0,0.05)",
              borderRadius: "8px",
            }}
          >
            <span role="img" aria-label="document" style={{ fontSize: "24px" }}>
              📄
            </span>
            <a
              href={`${API_UPLOAD_URL}${fileUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              download
            >
              {msg.meta.filename || msg.body || "Descargar documento"}
            </a>
          </div>
        );

      case "location":
        // Las coordenadas (lat y lng) deberías enviarlas en el objeto msg.meta desde tu backend
        const lat = msg.meta?.latitude;
        const lng = msg.meta?.longitude;
        const mapUrl = `https://www.google.com/maps?q=${lat},${lng}`;
        return (
          <div className="message-media location-message">
            <a
              href={mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "5px",
                textDecoration: "none",
                fontWeight: "bold",
              }}
            >
              <span role="img" aria-label="location">
                📍
              </span>{" "}
              Ver ubicación en mapa
            </a>
            {msg.body && (
              <div className="message-body text-sm mt-1">{msg.body}</div>
            )}
          </div>
        );

      case "text":
      default:
        return <div className="message-body">{msg.body}</div>;
    }
  };

  return (
    <div className="chat-window">
      <div className="chat-header">
        <h3 style={{ display: "inline" }}>
          {" "}
          {conversation.contact_name}{" "}
          <span>{conversation.phone_number.slice(2)}</span> | C.C.{" "}
          {conversation.dni}{" "}
        </h3>

        <BotModeToggle
          conversationId={conversation.id}
          initialMode={conversation.mode}
          // onModeChange={handleModeChange}
        />
      </div>

      <div className="messages-container">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`message ${msg.direction === "outbound" ? "outbound" : "inbound"}`}
            style={{ marginBottom: "15px" }} // Un poco de espacio extra entre mensajes
          >
            {/* Llamamos a la función que decide qué renderizar */}
            {renderMessageContent(msg)}

            <div
              className="message-timestamp text-xs"
              style={{ textAlign: "right", marginTop: "4px", opacity: 0.7 }}
            >
              {formatDate(msg.message_time)}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <Toolbar
        quickNotes={quickNotes}
        // Para notas de solo texto, forzamos file a null y type a 'text'
        onSendQuickNote={(text) => handleSendMessage(text, null, "text")}
        // Para notas con archivos o adjuntos manuales, pasamos la función directa
        // ya que Toolbar se encarga de enviarle (text, file, type)
        onSendFile={handleSendMessage}
      />

      <ChatInput
        onSendMessage={handleSendMessage}
        conversationId={conversation.id}
      />
    </div>
  );
}

export default ChatWindow;
